import type { HTMLAttributes } from 'react'
import { useRef } from 'react'
import { useSkeletonReveal } from '../kufic/skeleton-reveal'
import { Skeleton, SkeletonTable } from '../ui/skeleton'

/**
 * A screen loading, shaped like the screen: the trail, the title on its satr
 * with the actions at the end of the line, a strip of figures, then a ruled
 * table. The same 24px above the title that `PageShell` gives.
 */
export function ScreenSkeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  const ref = useRef<HTMLDivElement>(null)
  useSkeletonReveal(ref)

  return (
    <div
      ref={ref}
      {...props}
      data-slot="screen-skeleton"
      role="status"
      aria-busy="true"
      className={['grid gap-6 pt-6', className].filter(Boolean).join(' ')}
    >
      <div className="grid gap-2">
        <Skeleton className="h-2.5 w-40" />
        <div className="flex items-end justify-between gap-6 border-b border-border">
          <div className="-mb-px flex items-center gap-3 border-b-2 border-foreground pb-[9px]">
            {/* The mark, small: brand feedback in the header while the screen
                resolves. Unlabelled, so it stays decorative - this component's
                root region is already the announced status. */}
            <Skeleton className="size-5" />
            <Skeleton className="h-6 w-48" />
          </div>
          <Skeleton className="mb-2.5 h-control w-28" />
        </div>
        <Skeleton className="mt-0.5 h-2.5 w-80 max-w-full" />
      </div>
      <div className="grid gap-px border-y border-border-subtle bg-border-subtle md:grid-cols-3">
        {[0, 1, 2].map((item) => (
          <div key={item} className="grid gap-2 bg-card px-5 py-4">
            <Skeleton className="h-2.5 w-24" />
            <Skeleton className="h-5 w-32" />
          </div>
        ))}
      </div>
      <div aria-hidden="true">
        <SkeletonTable columns={[2, 1, 1, 1]} rows={6} headers={['', '', '', '']} />
      </div>
    </div>
  )
}
